/**
 * @biomax/plugin-wastewater — wastewater surveillance + RNA-to-cases
 * nowcasting.
 *
 * For shed pathogens (SARS-CoV-2, polio, MPXV, norovirus, hepatitis
 * A/E), wastewater RNA concentration leads clinical case detection
 * by 5-14 days and bypasses test-seeking-behaviour biases.
 *
 * v0.1 ships:
 *   - Per-pathogen shedding-curve catalog (gene copies / g stool ×
 *     time post-infection)
 *   - estimateIncidenceFromWastewater — invert observed gene-copies/L
 *     of sewage into estimated daily new infections via a discrete
 *     deconvolution
 *
 * Method (v0.1 simple): observed RNA at time t = Σ_s I(t-s) × shed(s)
 * × population_in_sewershed × (excretion_rate / sewage_volume). We
 * invert by least-squares deconvolution; v0.3 will use a Bayesian
 * approach with uncertainty quantification.
 */

import type { PluginManifest } from '@biomax/types';

export interface SheddingCurve {
  /** Pathogen catalog id. */
  pathogenId: string;
  /**
   * Shedding rate (log10 gene copies / g stool) at day s post-infection.
   * Index 0 = day 1.
   */
  log10CopiesPerGram: number[];
  /** Average daily stool mass per person (g). Default 200g. */
  stoolMassPerPersonGramsPerDay?: number;
  citations: { source: string; pmid?: string; note?: string }[];
}

export const SHEDDING_CATALOG: Record<string, SheddingCurve> = {
  'sars-cov-2-wildtype': {
    pathogenId: 'sars-cov-2-wildtype',
    log10CopiesPerGram: [3.0, 5.0, 7.0, 7.5, 7.0, 6.5, 6.0, 5.5, 5.0, 4.5, 4.0, 3.5, 3.0, 2.5, 2.0, 1.5, 1.0, 0.5, 0],
    citations: [
      { source: 'Wölfel et al. 2020 Nature', pmid: '32235945', note: 'Original Wuhan cohort stool shedding kinetics. PMID round-tripped 2026-05-28.' },
      { source: 'Wu et al. 2020 mSystems', pmid: '32694130', note: 'SARS-CoV-2 wastewater titers higher than clinically confirmed cases — early-detection lead-time evidence. PMID round-tripped 2026-05-28 (previously cited 35614120 in error; that PMID is unrelated rabbit-foot-perfusion paper).' },
      { source: 'Peccia et al. 2020 Nat Biotechnol', pmid: '32948856', note: 'Measurement of SARS-CoV-2 RNA in wastewater tracks community infection dynamics. PMID round-tripped 2026-05-28.' },
    ],
  },
  'polio-wpv1': {
    pathogenId: 'polio-wpv1',
    log10CopiesPerGram: [4.0, 6.0, 8.0, 8.5, 8.0, 7.5, 7.0, 6.5, 6.0, 5.5, 5.0, 4.5, 4.0, 3.5, 3.0, 2.5, 2.0, 1.5, 1.0, 0.5, 0],
    citations: [
      { source: 'Asghar et al. 2014 J Infect Dis', pmid: '25316848', note: 'Environmental surveillance for polioviruses in the Global Polio Eradication Initiative. PMID round-tripped 2026-05-28 (previously cited 25316867 in error; that PMID is the unrelated lab-containment paper).' },
    ],
  },
};

export interface NowcastParams {
  /** Observed daily wastewater gene copies / L sewage. */
  observedCopiesPerLiter: number[];
  /** Population in the sewershed catchment. */
  catchmentPopulation: number;
  /** Sewage volume per person per day (L). Default 200L = ~50 gal. */
  sewageVolumePerPersonPerDay?: number;
  /** Shedding curve to deconvolve against. */
  sheddingCurve: SheddingCurve;
}

export interface NowcastResult {
  /** Estimated daily new infections (same length as observedCopiesPerLiter). */
  estimatedIncidence: number[];
  /** Lead time in days between RNA signal and clinical-case signal (qualitative). */
  estimatedLeadDays: number;
}

/**
 * Estimate incidence from observed wastewater RNA via least-squares
 * deconvolution. Returns a positive-clipped estimate. v0.3 will swap
 * for a proper Bayesian deconvolution with credible intervals.
 */
export function estimateIncidenceFromWastewater(p: NowcastParams): NowcastResult {
  const stoolGramsPerDay = p.sheddingCurve.stoolMassPerPersonGramsPerDay ?? 200;
  const sewageLPerDay = p.sewageVolumePerPersonPerDay ?? 200;
  // Shedding kernel: copies per L of sewage per infected person at lag s.
  // = (10^log10CopiesPerGram[s] × stoolGramsPerDay) / sewageLPerDay
  const kernel = p.sheddingCurve.log10CopiesPerGram.map(
    log10 => (Math.pow(10, log10) * stoolGramsPerDay) / sewageLPerDay,
  );
  // Observed[t] = Σ_s I(t-s) × kernel[s] / catchmentPop
  // Inversion: greedy / non-negative deconvolution. For v0.1 simplicity,
  // iterative subtraction.
  const n = p.observedCopiesPerLiter.length;
  const incidence = new Array<number>(n).fill(0);
  const remaining = p.observedCopiesPerLiter.slice();
  // Iterate from earliest to latest; at each t, attribute the signal
  // not already explained by earlier-day shedding to incidence at t.
  for (let t = 0; t < n; t++) {
    // Compute predicted contribution from existing incidence[0..t-1].
    let predicted = 0;
    for (let s = 1; s < Math.min(kernel.length, t + 1); s++) {
      predicted += incidence[t - s]! * kernel[s]! / p.catchmentPopulation;
    }
    const residual = Math.max(0, remaining[t]! - predicted);
    // Attribute residual to incidence[t] via kernel[0] scaling.
    if (kernel[0]! > 0) {
      incidence[t] = (residual * p.catchmentPopulation) / kernel[0]!;
    }
  }
  // Heuristic lead estimate: peak of shedding curve.
  const peakDay = p.sheddingCurve.log10CopiesPerGram.indexOf(Math.max(...p.sheddingCurve.log10CopiesPerGram));
  return {
    estimatedIncidence: incidence,
    estimatedLeadDays: Math.max(0, 10 - peakDay), // qualitative
  };
}

/**
 * Citations for the wastewater plugin. PMIDs round-tripped via
 * @biomax/plugin-literature-fetch on verifiedAt. Re-run
 * scripts/verify-references.ts to revalidate.
 */
export const WASTEWATER_REFERENCES = [
  {
    id: 'peccia-2020',
    label: 'Peccia et al. 2020 Nat Biotechnol — "Measurement of SARS-CoV-2 RNA in wastewater tracks community infection dynamics"',
    pmid: '32948856',
    doi: '10.1038/s41587-020-0684-z',
    appliesTo: 'estimateIncidenceFromWastewater (deconvolution principle)',
    note: 'First peer-reviewed demonstration that wastewater RNA leads clinical-case dynamics. Underwrites the lead-time KPI in §7.12.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'wu-2020',
    label: 'Wu et al. 2020 mSystems — "SARS-CoV-2 Titers in Wastewater Are Higher than Expected from Clinically Confirmed Cases"',
    pmid: '32694130',
    doi: '10.1128/msystems.00614-20',
    appliesTo: 'SHEDDING_CATALOG (sars-cov-2-wildtype)',
    note: 'Quantifies the under-ascertainment ratio between wastewater RNA and reported cases — directly relevant to the deconvolution magnitude.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'wolfel-2020',
    label: 'Wölfel et al. 2020 Nature — "Virological assessment of hospitalized patients with COVID-2019"',
    pmid: '32235945',
    doi: '10.1038/s41586-020-2196-x',
    appliesTo: 'SHEDDING_CATALOG (sars-cov-2-wildtype shedding kinetics)',
    note: 'Wuhan cohort longitudinal stool + respiratory shedding. Underwrites the SARS-CoV-2 log10CopiesPerGram time series.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'asghar-2014',
    label: 'Asghar et al. 2014 J Infect Dis — "Environmental Surveillance for Polioviruses in the Global Polio Eradication Initiative"',
    pmid: '25316848',
    doi: '10.1093/infdis/jiu384',
    appliesTo: 'SHEDDING_CATALOG (polio-wpv1)',
    note: 'Operational reference for the GPEI environmental-surveillance program. Underwrites the polio shedding curve.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'hellmer-2014',
    label: 'Hellmer et al. 2014 Appl Environ Microbiol — "Detection of Pathogenic Viruses in Sewage Provided Early Warnings of Hepatitis A Virus and Norovirus"',
    pmid: '25172863',
    doi: '10.1128/aem.01981-14',
    appliesTo: 'estimateIncidenceFromWastewater (early-warning lead-time)',
    note: 'Pre-COVID demonstration of wastewater early-warning for HAV + norovirus. Generalises the deconvolution principle beyond SARS-CoV-2.',
    verifiedAt: '2026-05-28',
  },
] as const;

export const manifest: PluginManifest = {
  id: 'wastewater', version: '0.1.0',
  engineCompat: '^0.5.0', kingdoms: '*', systems: [], components: [],
};
